// Considered: ContextSegmentInspectorMolecule's former inline accordion body —
// rejected because it lived inside the collapsible list and reflowed sibling
// segment rows on expand, which is exactly the reflow the rail drill-in (SC4)
// exists to stop. This molecule is the segment's own overlay (mirrors the level
// Filters drill-in, `ContextLevelInspectorOrganism`), mounted by the host as a
// full rail overlay so editing one segment never moves the others. See spec
// context-selector-editor.md Phase 3 (SC4) Reuse Audit.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import LocalizedInputAtom from '../../atoms/LocalizedInput/LocalizedInputAtom';
import type { LocalizedValue } from '../../atoms/LocalizedInput/LocalizedInputAtom';
import UnifiedFilterTreeMolecule from '../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { withStableFilterGroupIds } from '../../../utils/filterGroupIds';
import { seedLeavesIntoGroups, topLevelRowToLeaf } from '../../../utils/filterGroupSeeding';
import { simpleGroupToUnified, unifiedGroupToSimple } from '../../../utils/contextFlowSerialization';
import AdvancedFilterEditorOrganism from '../../organisms/AdvancedFilterEditor/AdvancedFilterEditorOrganism';
import type { FilterState } from '../../../types/filterState';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter';
import type { RelationshipOption } from '../RelationshipFilter';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { ContextFlowSegment } from '../../../types/contextFlow';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { FilterGroup, FilterGroupLeaf } from 'types/filters';
import { nextFilterId, withStableIds } from '../../../utils/ids';
import { cfGroupStats } from '../../../utils/cfGroup';
import './SegmentPanelMolecule.scss';

const EMPTY_FILTER: FilterState = { propertyFilters: [], perspectiveFilters: [] };

/** Options for the (new, non-persisted) per-segment page-size UI — `undefined` means "All". */
const PAGE_SIZE_OPTIONS: Array<{ label: string; value: number | undefined }> = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: 'all', value: undefined },
];

export interface SegmentPanelMoleculeProps {
  segment: ContextFlowSegment;
  index: number;
  levelColor?: string;
  levelSortBy?: string;
  levelSortDir?: 'asc' | 'desc';
  /** Properties available for the segment filter editor. */
  availableProperties: PropertyOption[];
  /** Unscoped org-wide catalog powering the advanced editor's scope-aware rail. */
  orgWideProperties?: PropertyOption[];
  /** The parent level's eligible schema `system_name`s — the rail's fallback scope. */
  scopeTypes?: string[];
  /** Perspectives available for the segment filter editor. */
  availablePerspectives: PerspectiveOption[];
  /** Relationships available for the segment filter editor. */
  availableRelationships?: RelationshipOption[];
  /** Schemas for the Sphere Type/Family chip-selector value editor. */
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /** Published arc templates for the arc filter lane. */
  arcTemplates?: ArcTemplateOption[];
  /** Props for PerspectiveSearch (assignee narrowing) — assignee filtering disabled if not provided. */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * The real six-lane catalog (`useUnifiedFilterCatalog`, wired by the host
   * page) — forwarded verbatim to this panel's own advanced dialog. Omitted
   * renders the tree's add-condition button as a no-op skeleton.
   */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  onChangeSegment: (partial: Partial<ContextFlowSegment>) => void;
  onRemove: () => void;
  onBack: () => void;
  readOnly?: boolean;
}

const SegmentPanelMolecule: React.FC<SegmentPanelMoleculeProps> = ({
  segment,
  index,
  levelColor = 'var(--primary-color)',
  levelSortBy,
  levelSortDir = 'asc',
  availableProperties,
  orgWideProperties,
  scopeTypes,
  availablePerspectives,
  availableRelationships = [],
  arcTemplates = [],
  familyTypeSchemas = [],
  perspectiveSearchProps,
  renderCatalog,
  onChangeSegment,
  onRemove,
  onBack,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const segmentFilters = segment.filters ?? EMPTY_FILTER;
  const hasSortOverride = segment.sortOverride != null;

  // ── Segment filter — the embedded tree (everySurfaceMountsTheSameTree) ──────
  // Mirrors ContextLevelInspectorOrganism's rootGroup/handleTreeChange fold: no
  // bespoke row list, no full-screen drill-in overlay — a single
  // `UnifiedFilterTreeMolecule` mount. Top-level `propertyFilters` fold into the
  // root's own leaves (an AND group gets them appended; an OR root gets a new
  // leading AND group).
  const propertyFilters = useMemo(
    () => withStableIds(segmentFilters.propertyFilters ?? []),
    [segmentFilters]
  );

  const rootGroup = useMemo<FilterGroup>(() => {
    const groups = segmentFilters.filterGroups ?? [];
    const seededGroups = seedLeavesIntoGroups(groups, propertyFilters.map(topLevelRowToLeaf));
    const stableGroups = withStableFilterGroupIds(seededGroups);
    if (stableGroups.length === 1) return simpleGroupToUnified(stableGroups[0]);
    return {
      id: nextFilterId(),
      operator: 'and',
      expressions: [],
      groups: stableGroups.map(simpleGroupToUnified),
    };
  }, [segmentFilters.filterGroups, propertyFilters]);

  const filterCount = useMemo(() => cfGroupStats(rootGroup).conditions, [rootGroup]);

  const handleTreeChange = useCallback(
    (next: FilterGroup) => {
      onChangeSegment({ filters: { ...segmentFilters, propertyFilters: [], filterGroups: [unifiedGroupToSimple(next)] } });
    },
    [segmentFilters, onChangeSegment]
  );

  if (advancedOpen) {
    return (
      <AdvancedFilterEditorOrganism
        filter={segmentFilters}
        onApply={(filters: FilterState) => {
          onChangeSegment({ filters });
          setAdvancedOpen(false);
        }}
        onBack={() => setAdvancedOpen(false)}
        label={segment.label.default || t('context.editor.inspector.segments.newSegmentLabel')}
        availableProperties={availableProperties}
        orgWideProperties={orgWideProperties}
        scopeTypes={scopeTypes}
        availablePerspectives={availablePerspectives}
        availableRelationships={availableRelationships}
        arcTemplates={arcTemplates}
        familyTypeSchemas={familyTypeSchemas}
        perspectiveSearchProps={perspectiveSearchProps}
        renderCatalog={renderCatalog}
        readOnly={readOnly}
      />
    );
  }

  return (
    <div className="segment-panel-molecule" data-testid="segment-panel">
      <div className="segment-panel-molecule__header">
        <button type="button" className="segment-panel-molecule__back" onClick={onBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          {t('context.editor.inspector.segments.title')}
        </button>
        <span className="segment-panel-molecule__spacer" />
        {!readOnly && (
          <button
            type="button"
            className="segment-panel-molecule__remove"
            onClick={onRemove}
            data-testid="segment-panel-remove"
          >
            <i className="pi pi-trash" aria-hidden="true" />
            {t('context.editor.inspector.segments.remove')}
          </button>
        )}
      </div>

      <div className="segment-panel-molecule__body">
        <div className="segment-panel-molecule__bucket-row">
          <span className="segment-panel-molecule__num-badge" style={{ background: levelColor }} aria-hidden="true">
            {index + 1}
          </span>
          <span className="segment-panel-molecule__bucket-eyebrow">
            {t('context.editor.inspector.segments.bucketEyebrow', { n: index + 1 })}
          </span>
        </div>

        {/* Bucket label */}
        <div className="segment-panel-molecule__field">
          <div className="segment-panel-molecule__field-label">
            {t('context.editor.inspector.segments.bucketLabel')}
          </div>
          <LocalizedInputAtom
            value={segment.label}
            onChange={(label: LocalizedValue) => onChangeSegment({ label })}
            disabled={readOnly}
            className="segment-panel-molecule__label-input"
            aria-label={t('context.editor.inspector.segments.bucketLabel')}
          />
        </div>

        {/* Segment filter — everySurfaceMountsTheSameTree: the same canonical tree
            every other surface mounts, no bespoke row list, no separate overlay. */}
        <div className="segment-panel-molecule__field">
          <div className="segment-panel-molecule__field-label-row">
            <span className="segment-panel-molecule__field-label">
              {t('context.editor.inspector.segments.filterLabel')}
            </span>
            <ChipAtom
              label={t('context.editor.inspector.segments.andWithLevel')}
              className="segment-panel-molecule__and-chip"
            />
            {filterCount > 0 && (
              <span className="segment-panel-molecule__filter-count-badge">
                {filterCount} {t('context.editor.inspector.level.filtersActive')}
              </span>
            )}
            <span className="segment-panel-molecule__spacer" />
            {!readOnly && (
              <button
                type="button"
                className="segment-panel-molecule__advanced-btn"
                onClick={() => setAdvancedOpen(true)}
                data-testid="segment-panel-advanced"
              >
                <i className="pi pi-sliders-h" aria-hidden="true" />
                {t('context.editor.inspector.level.advanced')}
              </button>
            )}
          </div>
          <UnifiedFilterTreeMolecule
            value={rootGroup}
            onChange={handleTreeChange}
            availableProperties={availableProperties}
            arcTemplates={arcTemplates}
            availableRelationships={availableRelationships}
            availablePerspectives={availablePerspectives}
            familyTypeSchemas={familyTypeSchemas}
            perspectiveSearchProps={perspectiveSearchProps}
            renderCatalog={renderCatalog}
            readOnly={readOnly}
            emptyNote={t('context.editor.inspector.segments.filtersEmpty')}
            dense
            datumScope="segment-filter-tree"
            container="ui-tree"
            className="segment-panel-molecule__filter-tree"
            dnd
            defaultOpen
          />
        </div>

        {/* Sort override */}
        <div className="segment-panel-molecule__field">
          <div className="segment-panel-molecule__field-label-row">
            <span className="segment-panel-molecule__field-label">
              {t('context.editor.inspector.segments.sortLabel')}
            </span>
            <SwitchAtom
              checked={hasSortOverride}
              onChange={(checked) =>
                onChangeSegment({
                  sortOverride: checked ? { property: levelSortBy ?? 'name', dir: levelSortDir } : null,
                })
              }
              disabled={readOnly}
              aria-label={t('context.editor.inspector.segments.overrideToggle')}
            />
          </div>
          {hasSortOverride ? (
            <div className="segment-panel-molecule__sort-override" data-testid={`segment-panel-sort-override-${segment.id}`}>
              <span className="segment-panel-molecule__sort-property">{segment.sortOverride!.property}</span>
              <span className="segment-panel-molecule__sort-dir">
                {segment.sortOverride!.dir === 'asc'
                  ? t('context.editor.inspector.segments.asc')
                  : t('context.editor.inspector.segments.desc')}
              </span>
            </div>
          ) : (
            <div className="segment-panel-molecule__sort-inherit" data-testid={`segment-panel-sort-inherit-${segment.id}`}>
              <i className="pi pi-arrow-up" aria-hidden="true" />
              {t('context.editor.inspector.segments.inheritsSort', {
                sort: `${levelSortBy ?? 'name'} ${levelSortDir}`,
              })}
            </div>
          )}
        </div>

        {/* Page size — new UI (SC4); the field round-trips through editor state but
            the persisted-context serializer intentionally drops it (no per-segment
            limit exists server-side yet), so it never reaches saved data. */}
        <div className="segment-panel-molecule__field">
          <div className="segment-panel-molecule__field-label-row">
            <span className="segment-panel-molecule__field-label">
              {t('context.editor.inspector.segments.pageSize')}
            </span>
            <span className="segment-panel-molecule__spacer" />
            <div className="segment-panel-molecule__page-size-group">
              {PAGE_SIZE_OPTIONS.map((opt) => {
                const active = (segment.pageSize ?? undefined) === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    className={`segment-panel-molecule__page-size-btn${active ? ' segment-panel-molecule__page-size-btn--active' : ''}`}
                    onClick={() => onChangeSegment({ pageSize: opt.value })}
                    disabled={readOnly}
                    data-testid={`segment-panel-page-size-${opt.label}`}
                  >
                    {opt.label === 'all' ? t('context.editor.inspector.segments.pageSizeAll') : opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentPanelMolecule;
